/*
 * Copyright (c) 2024. Spin in het Web B.V. Apeldoorn NL.
 * Deze software is géén open source. Het auteursrecht van deze software
 * ligt bij Spin in het Web B.V. en/of Jellybean B.V. en/of de auteur.
 * Gebruik van deze software is onderhevig aan een licentieovereenkomst.
 * www.spininhetweb.nl
 */

/**
 * sihwdatumveld.ts
 * 20-12-2024 - Jelmer Jellema - Spin in het Web B.V. Apeldoorn NL
 * Component Sihwdatumveld
 * Selector: sihwdatumveld
 *
 * Eenvoudige datuminput met validatie en rewrite
 * <sihwdatumveld [(ngModel)=....]
 * parset en display een Nederlandse datum (1 of 2 cijfers voor dag/maand, 2 of 4 voor jaar)
 * geeft altijd dd-MM-yyyy weer en slaat altijd sqldate op (verwacht ook een sqldate waarde)
 * Het jaar mag weggelaten worden: bij alleen dag en maand vullen we het jaar aan waarin die
 * dag-maand als eerstvolgende valt, vandaag meegerekend. Op 19-8-2026 wordt 30-12 dus
 * 30-12-2026 en 19-1 wordt 19-1-2027. Met het attribuut autoJaarStrikt wordt het altijd het
 * huidige jaar, voor velden waar een datum in het verleden normaal is.
 * Overgenomen attributen: placeholder, size
 *
 */
import {booleanAttribute, Component, HostBinding, Input} from "@angular/core";
import {
    AbstractControl,
    ControlValueAccessor, FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR, ValidationErrors,
    Validator
} from "@angular/forms";
import {Sihwlog} from "../sihwlog";
import {DateTime} from "luxon";


@Component({
    standalone: true,
    selector: 'sihwdatumveld',
    templateUrl: './sihwdatumveld.html',
    styleUrls: ['sihwdatumveld.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: Sihwdatumveld, multi: true },
        { provide: NG_VALIDATORS, useExisting: Sihwdatumveld, multi: true } //doorgeven valid state
    ],
    imports: [
    FormsModule
]
})
export class Sihwdatumveld  implements ControlValueAccessor, Validator {
    private readonly validFormats = [
        'd-M-yyyy',
        'd-M-yy'
    ];
    //formats zonder jaar: het jaar vullen we zelf aan, zie vulJaarAan()
    private readonly dagMaandFormats = [
        'd-M'
    ];
    private readonly outputFormat = 'd-M-yyyy';
    private log: Sihwlog = new Sihwlog();
    ctrlval: string | null = null; //onze ngmodel var
    private value: DateTime | null = null;
    private valid: boolean = true; //vinden wij het valid
    private verandering: boolean = false; //is er een change geweest? Gebruikt bij blur
    private meldGewijzigd: (_: any) => void = (_) => {
    }; //controlvalueaccessor
    onTouched: () => void = () => {
    }; //controlvalueaccessor


    @Input('disabled') disabled: boolean = false; //de disabled value, en readonly op de host. Dat gebruiken we als alternatief voor disabled, wat we niet goed kunnen zetten
    @HostBinding('attr.readonly') get hostReadonly() {
        return this.disabled ? true : null;
    } //zorg voor verwijderen readonly als niet nodig

    //copy props
    @Input() placeholder: string = '';
    @Input() size: Number = 5; //er moet een default zijn

    /**
     * Bepaalt welk jaar we aanvullen als de gebruiker alleen dag en maand invult. Standaard
     * (false) wordt dat het jaar waarin die dag-maand als eerstvolgende valt, dus een datum
     * die dit jaar al voorbij is komt in het volgende jaar terecht. Op true blijft het altijd
     * het huidige jaar: gebruik dat bij velden waar een datum in het verleden normaal is,
     * zoals een geboortedatum of een ingangsdatum die je achteraf invoert.
     */
    @Input({transform: booleanAttribute}) autoJaarStrikt: boolean = false;
    ///////////////////////////////////////// CONTROLVALUEACCESSOR //////////////////
    /**
     * Nieuwe waarde wordt aangeboden door ngmodel enzo
     * @param obj
     */
    writeValue(obj: any): void {
        //de waarde komt als string binnen, in ons interne format
        this.log.debug(`writeValue`,obj);
        //voor de zekerheid. Een lege waarde is geen datum, maar ook geen fout: modellen
        //hebben vaak een lege string als default en dat is gewoon "nog niets ingevuld"
        if (obj === null || obj === undefined || obj === '')
        {
            this.value = null;
            this.valid = true;
            this.ctrlval = null;
        }
        else{
            this.value = DateTime.fromSQL(obj); //moet in YYYY-MM-DD zijn
            if (this.value.isValid)
            {
                this.valid = true;
                this.ctrlval = this.toString();
            }
            else {
                this.ctrlval = obj; //dan maar letterlijk
                this.value = null;
                this.valid = false;
            }
        }
    }

    registerOnChange(fn: any): void {
        this.meldGewijzigd = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.log.debug(`sihwdatumveld setDisabled`, isDisabled);
        this.disabled = isDisabled;
    }

    /////////////////////////////// Validator ////////////////////////////////
    validate(control: AbstractControl): ValidationErrors | null {
        //dit hebben we al gedaan
        this.log.debug(`sihwdatumveld validate`, this.valid);
        return this.valid ? null : {sihwdatumveld: true};
    }

    //////////////////////////////// MODEL //////////////////////////////////////////

    /**
     * 1. Binnenkomende waarde: zet de juiste stringrepresentatie in ons veld. That's all. Blijf van de waarde af
     * 2. Gewijzigde waarde: parse en bewaar de juiste stringrepresentatie. Stuur hem naar boven
     * 3. Blur  / Enter: zet de juiste stringrepresentatie als er gewijzigd is
     */

    /**
     * ngOnChange
     */
    valueChanged() {
        //opnieuw parsen
        this.log.debug(`nieuwe waarde ingevuld`);
        this.parseCtrlVal(); //zet this.value
        //we melden geen invalid waarden
        this.meldGewijzigd(this.valid && this.value ? this.value.toSQLDate() : null);
        this.verandering = true; //zie ctrlBlur
    }

    /**
     * Blur: werk de waarde bij en ga door
     */
    ctrlBlur() {
        this.werkCtrlValBij(); //weergave
        //sowieso touched
        this.onTouched();
    }

    //enterkey: ook de display waarde bijwerken
    enterkey(event: Event) {
        this.werkCtrlValBij();
        //en niet door
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * Helper bij blur en enterkey
     */
    werkCtrlValBij() {
        if (this.verandering && this.valid) {
            this.ctrlval = this.toString(); //mooi format
        }
        this.verandering = false;
    }

    /////////////////////////////////////////////////////////////////////////////////
    /**
     * Helper: zet this.value op uit de ctrlval, en zet this.valid
     */
    parseCtrlVal() {

        if (!(this.ctrlval && this.ctrlval.length)) {
            this.valid = true; //we vinden het prima. Required/ empty validators gaan apart
            this.value = null;
            return;
        }
        this.value = null;
        for(let format of this.validFormats)
        {
            let dt = DateTime.fromFormat(this.ctrlval, format);
            if (dt.isValid)
            {
                this.value = dt;
                break;
            }
        }
        if (!this.value)
        {
            //geen jaar ingevuld? Dan vullen we dat zelf aan
            for(let format of this.dagMaandFormats)
            {
                let dt = DateTime.fromFormat(this.ctrlval, format);
                if (dt.isValid)
                {
                    this.value = this.vulJaarAan(dt);
                    break;
                }
            }
        }
        if (this.value)
        {
            //geldig
            this.valid = true;
        }
        else {
            this.value = null;
            this.valid = false;
        }
        this.log.debug(`parseVal`, this.ctrlval, this.value, this.value?.toSQLDate());
    }

    /**
     * Bepaalt het jaar bij een datum waarin alleen dag en maand zijn ingevuld. Luxon heeft er
     * het huidige jaar in gezet; dat houden we zo bij autoJaarStrikt, en anders schuift een
     * datum die dit jaar al voorbij is een jaar op naar de eerstvolgende keer dat hij valt.
     * @param dt de geparste datum, met het huidige jaar erin
     */
    private vulJaarAan(dt: DateTime): DateTime {
        return (this.autoJaarStrikt || dt >= DateTime.now().startOf('day')) ? dt : dt.plus({years: 1});
    }

    /**
     * Zet onze interne tijdrepresentatie om naar een string of null bij null
     */
    private toString(): string | null {
        if (this.value === null) {
            return null;
        }
        return this.value.toFormat(this.outputFormat);
    }
}
